import { useState, useContext, useMemo, useEffect, useRef, useCallback } from "react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { toast } from "@/hooks/useToast";
import { FileText, Calendar, Edit3, Map, CheckCircle, CreditCard, MessageSquare, Activity } from "lucide-react";

import { TravelRequestModal } from "@/lib/requests";
import { Modal } from "@/components/ui/modal";
import { DashboardItinerary, DashboardTravelRequest } from "@/lib/models/dashboard";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useItineraries } from "@/hooks/useItineraries";

import { DashboardItinerariesTab } from "@/lib/dashboard/DashboardItinerariesTab";
import { DashboardRequestsTab } from "@/lib/dashboard/DashboardRequestsTab";
import { ItineraryTabConfig } from "@/lib/dashboard/types";

const Dashboard = () => {
  const { t } = useI18n();

  const {
    itineraries: allItineraries,
    travelRequests,
    loading,
    error,
    refreshData,
    acceptTravelRequest,
    declineTravelRequest,
    assignTravelRequest,
  } = useDashboardData();

  const { deleteItinerary, duplicateItinerary, updateItineraryStatus } =
    useItineraries({ disableAutoFetch: true });

  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("Dashboard must be used inside an AuthProvider");
  const userRole = authContext.userRole;
  const currentUserId = authContext.user?.id;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();
  const activeTab = searchParams.get("tab") || (userRole === "traveler" ? "proposals" : "draft");
  const selectedRequestId = searchParams.get("requestId");

  // ── Modal state ──────────────────────────────────────────────────────────
  const [selectedTravelRequest, setSelectedTravelRequest] = useState<DashboardTravelRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequestForItinerary, setSelectedRequestForItinerary] = useState<DashboardTravelRequest | null>(null);
  const [isCreateItineraryModalOpen, setIsCreateItineraryModalOpen] = useState(false);

  // ── Refresh flags ────────────────────────────────────────────────────────
  const [refreshingRequests, setRefreshingRequests] = useState(false);
  const [refreshingItineraries, setRefreshingItineraries] = useState(false);

  // ── Derived itinerary lists (badge counts only) ──────────────────────────
  // La tab "Attivi" esiste solo per chi lavora sui viaggi. Il viaggiatore ha
  // "Proposte" per sended/request_changes e continua a vedere i confermati in
  // "Pianificati" anche col saldo a rate aperto: senza questo guardia-ruolo
  // sparirebbero da entrambe le sue tab.
  const hasActiveTab = userRole !== "traveler";
  const isActive = useCallback(
    (it: DashboardItinerary) => hasActiveTab && it.isActive,
    [hasActiveTab]
  );

  const activeItineraries = useMemo(() => allItineraries.filter(isActive), [allItineraries, isActive]);
  const draftItineraries = useMemo(() => allItineraries.filter((it) => it.status !== "completed" && !isActive(it)), [allItineraries, isActive]);
  const proposalsItineraries = useMemo(() => allItineraries.filter((it) => it.status === "sended" || it.status === "request_changes"), [allItineraries]);
  const plannedItineraries = useMemo(() => { return allItineraries.filter((it) => (it.detailedStatus === 'upcoming' || it.detailedStatus === 'ongoing') && !isActive(it)); }, [allItineraries, isActive]);
  const finishedItineraries = useMemo(() => { return allItineraries.filter((it) => it.detailedStatus === 'past'); }, [allItineraries]);

  // ── Refresh handlers ─────────────────────────────────────────────────────
  const handleRefreshRequests = async () => {
    setRefreshingRequests(true);
    await refreshData();
    setRefreshingRequests(false);
  };

  const handleRefreshItineraries = async () => {
    setRefreshingItineraries(true);
    await refreshData();
    setRefreshingItineraries(false);
  };

  const handleUpdateItineraryStatus = async (id: string, status: any) => {
    const result = await updateItineraryStatus(id, status);
    await refreshData();
    return result;
  };

  // ── Modal handlers (called by tab components) ────────────────────────────
  const handleViewRequest = (request: DashboardTravelRequest) => {
    setSelectedTravelRequest(request);
    setIsModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setIsModalOpen(false);
    setSelectedTravelRequest(null);

    if (!selectedRequestId) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    setSearchParams(nextParams);
  };

  const handleCreateItinerary = (request: DashboardTravelRequest) => {
    setSelectedRequestForItinerary(request);
    setIsCreateItineraryModalOpen(true);
  };

  const handleStartBuilding = () => {
    navigate(
      selectedRequestForItinerary
        ? `/itinerary-builder?requestId=${selectedRequestForItinerary.id}`
        : "/itinerary-builder"
    );
  };

  const handleViewTravelRequest = (id: string) => {
    const req = travelRequests.find((r) => r.id === id);
    if (req) {
      setSelectedTravelRequest(req);
      setIsModalOpen(true);
    }
  };

  // ── Itinerary action handlers ────────────────────────────────────────────
  const handleEditItinerary = (id: string) =>
    navigate(`/itinerary-builder?itineraryId=${id}`);

  const handleDeleteItinerary = async (id: string) => {
    const success = await deleteItinerary(id);
    if (success) await refreshData();
  };

  const handleDuplicateItinerary = async (id: string) => {
    await duplicateItinerary(id);
    await refreshData();
  };

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  // ── Deep link a una travel request (?requestId=…) ─────────────────────────
  // Tipicamente si arriva da una notifica mentre la dashboard è già montata:
  // `useDashboardData` fetcha solo al mount, quindi la lista in memoria è
  // anteriore alla richiesta appena arrivata e la find() fallirebbe in silenzio.
  // Quando l'id non è in lista si rilegge una volta sola, poi si dichiara forfait.
  const refreshedForRequestRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedRequestId) {
      refreshedForRequestRef.current = null;
      return;
    }

    const request = travelRequests.find((item) => item.id === selectedRequestId);
    if (request) {
      setSelectedTravelRequest(request);
      setIsModalOpen(true);
      return;
    }

    // Fetch già in corso: si rivaluta quando arrivano i dati.
    if (loading) {
      return;
    }

    if (refreshedForRequestRef.current !== selectedRequestId) {
      refreshedForRequestRef.current = selectedRequestId;
      void refreshData();
      return;
    }

    // Rilettura fatta e la richiesta continua a non esserci: eliminata, oppure
    // non visibile con i permessi correnti. Meglio dirlo che non aprire nulla.
    toast({
      title: String(t("dashboard.requestNotFoundTitle")),
      description: String(t("dashboard.requestNotFoundDesc")),
      variant: "destructive",
    });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    setSearchParams(nextParams, { replace: true });
  }, [selectedRequestId, travelRequests, loading, refreshData, searchParams, setSearchParams, t]);

  // ── Tab header config (role-aware, drives badge counts) ──────────────────
  const tabs = useMemo(() => {
    if (userRole === "traveler") {
      return [
        { value: "planned", label: t("dashboard.tabPlanned"), icon: Calendar, badge: plannedItineraries.length.toString() },
        { value: "proposals", label: t("dashboard.tabProposals"), icon: CreditCard, badge: proposalsItineraries.length.toString() },
        { value: "finished", label: t("dashboard.tabFinished"), icon: CheckCircle, badge: finishedItineraries.length.toString() },
      ];
    }
    return [
      { value: "requests", label: t("dashboard.tabRequests"), icon: FileText, badge: travelRequests.length.toString() },
      { value: "draft", label: t("dashboard.tabDrafts"), icon: Edit3, badge: draftItineraries.length.toString() },
      { value: "active", label: t("dashboard.tabActive"), icon: Activity, badge: activeItineraries.length.toString() },
      { value: "planned", label: t("dashboard.tabPlanned"), icon: Calendar, badge: plannedItineraries.length.toString() },
      { value: "finished", label: t("dashboard.tabFinished"), icon: CheckCircle, badge: finishedItineraries.length.toString() },
    ];
  }, [userRole, t, travelRequests.length, draftItineraries.length, proposalsItineraries.length, activeItineraries.length, plannedItineraries.length, finishedItineraries.length]);

  // ── Itinerary tab configs (pure data – no state setters) ─────────────────
  const itineraryTabConfigs: ItineraryTabConfig[] = useMemo(() => {
    const all: ItineraryTabConfig[] = [
      {
        type: "itineraries",
        value: "draft",
        labelKey: "dashboard.tabDrafts",
        icon: Edit3,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "agency"],
        data: draftItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.draftCount",
          noCountKey: "dashboard.noDraftItineraries",
          emptyTitleKey: "dashboard.noDraftItinerariesYet",
          emptyTitleDesignerKey: "dashboard.noDraftItinerariesForDesigner",
          emptyDescKey: "dashboard.createFirstItinerary",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        type: "itineraries",
        value: "proposals",
        labelKey: "dashboard.tabProposals",
        icon: CreditCard,
        visibleTo: ["traveler"],
        data: proposalsItineraries,
        showDesignerFilter: false,
        i18n: {
          countKey: "dashboard.proposalsCount",
          noCountKey: "dashboard.noProposalsItineraries",
          emptyTitleKey: "dashboard.noProposalsItineraries",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        type: "itineraries",
        value: "active",
        labelKey: "dashboard.tabActive",
        icon: Activity,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "agency"],
        data: activeItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.activeCount",
          noCountKey: "dashboard.noActiveItineraries",
          emptyTitleKey: "dashboard.noActiveItineraries",
          emptyTitleDesignerKey: "dashboard.noActiveItinerariesForDesigner",
          emptyDescKey: "dashboard.activeItinerariesDesc",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        type: "itineraries",
        value: "planned",
        labelKey: "dashboard.tabPlanned",
        icon: Calendar,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "traveler", "agency"],
        data: plannedItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.plannedCount",
          noCountKey: "dashboard.noPlannedItineraries",
          emptyTitleKey: "dashboard.noPlannedItineraries",
          emptyTitleDesignerKey: "dashboard.noItinerariesForDesigner",
          emptyDescKey: "dashboard.completeFirstItinerary",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadCompletedError",
        },
      },
      {
        type: "itineraries",
        value: "finished",
        labelKey: "dashboard.tabFinished",
        icon: CheckCircle,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "traveler", "agency"],
        data: finishedItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.finishedCount",
          noCountKey: "dashboard.noFinishedItineraries",
          emptyTitleKey: "dashboard.noFinishedItineraries",
          emptyTitleDesignerKey: "dashboard.noItinerariesForDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
    ];
    return all.filter((cfg) => !cfg.visibleTo || cfg.visibleTo.includes(userRole ?? ""));
  }, [userRole, draftItineraries, proposalsItineraries, activeItineraries, plannedItineraries, finishedItineraries]);

  return (
    <PrivateLayout
      pageTitle={t("dashboard.pageTitle")}
      pageDescription={t("dashboard.pageDescription")}
      showBackButton={false}
      showCtaButton={userRole !== "traveler"}
    >
      <div className="space-y-6">
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <div className="max-w-7xl md:px-6 mx-auto">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            {(userRole === "travel_designer" ||
              userRole === "travel_advisor_member" ||
              userRole === "admin" ||
              userRole === "agency") && (
              <ProfileTabsContent value="requests">
                <DashboardRequestsTab
                  userRole={userRole}
                  travelRequests={travelRequests}
                  loading={loading}
                  error={error}
                  onRefresh={handleRefreshRequests}
                  refreshing={refreshingRequests}
                  onViewRequest={handleViewRequest}
                  onCreateItinerary={handleCreateItinerary}
                />
              </ProfileTabsContent>
            )}

            {itineraryTabConfigs.map((cfg) => (
              <ProfileTabsContent key={cfg.value} value={cfg.value}>
                <DashboardItinerariesTab
                  config={cfg}
                  userRole={userRole}
                  travelRequests={travelRequests}
                  loading={loading}
                  error={error}
                  onEdit={handleEditItinerary}
                  onDelete={handleDeleteItinerary}
                  onDuplicate={handleDuplicateItinerary}
                  onViewTravelRequest={handleViewTravelRequest}
                  onUpdateStatus={handleUpdateItineraryStatus}
                  onRefresh={handleRefreshItineraries}
                  refreshing={refreshingItineraries}
                />
              </ProfileTabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* Travel request detail modal */}
      <TravelRequestModal
        isOpen={isModalOpen}
        onClose={handleCloseRequestModal}
        travelRequest={selectedTravelRequest}
        onAccept={async (id: string) => await acceptTravelRequest(id)}
        onDecline={async (id: string) => await declineTravelRequest(id)}
        onAssign={async (id: string, designerId: string) =>
          await assignTravelRequest(id, designerId, userRole === "agency" ? "pending" : "assigned")
        }
        isAdmin={userRole === "admin"}
        agencyId={userRole === "agency" ? currentUserId : undefined}
        showActions={
          (userRole === "admin" &&
            selectedTravelRequest?.status === "pending") ||
          (userRole === "agency" &&
            selectedTravelRequest?.status === "pending") ||
          ((userRole === "travel_designer" ||
            userRole === "travel_advisor_member") &&
            selectedTravelRequest?.status === "pending" &&
            !selectedTravelRequest?.isExpired)
        }
      />

      {/* Create itinerary confirmation modal */}
      <Modal
        isOpen={isCreateItineraryModalOpen}
        onClose={() => {
          setIsCreateItineraryModalOpen(false);
          setSelectedRequestForItinerary(null);
        }}
        title=""
        size="sm"
        showHeader={false}
        footer={
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateItineraryModalOpen(false);
                setSelectedRequestForItinerary(null);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleStartBuilding}
              className="flex items-center gap-2"
            >
              {t("dashboard.startBuilding")}
            </Button>
          </div>
        }
      >
        <div className="text-center py-20">
          <div className="flex justify-center mb-4">
            <Map className="h-8 w-8" />
          </div>
          <h2 className="text-gray-900 mb-2">{t("dashboard.readyToCreate")}</h2>
          <p className="text-gray-600">
            {t("dashboard.eachItineraryIndependent")}
          </p>
        </div>
      </Modal>
    </PrivateLayout>
  );
};

export default Dashboard;